import React, { forwardRef, useEffect, useRef, useState, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  GLOBAL_MILITARY_BASES,
  CONFLICT_ZONES,
  CATEGORY_COLORS,
  CONFLICT_STATUS_COLORS,
  CONFLICT_BORDER_COLORS,
} from '../data/intelligence-overlays';

interface MilitaryBase {
  id: number;
  name: string;
  coordinates: [number, number];
  type: string;
  country: string;
  info: string;
}

interface WaybackMapProps {
  activeReleaseId: string | number | null;
  selectedLocation: any;
  showLabels: boolean;
  showMilitaryBases?: boolean;
  militaryBases?: MilitaryBase[];
  drawingMode?: 'rectangle' | 'polygon' | null;
  onAOIDraw?: (geometry: { type: string; coordinates: number[][][] }) => void;
  drawnAOI?: { type: string; coordinates: number[][][] } | null;
  savedAOIs?: Array<{ id: string; name: string; geometry: { type: string; coordinates: number[][][] }; metadata?: { ml_score?: number } }>;
  selectedAOIId?: string | null;
  releases?: Array<{
    id: string | number;
    tileTemplate?: string;
    title?: string;
    hasLocalChanges?: boolean;
    footprint?: { type: string; coordinates: number[][][] | number[][] } | null;
  }>;
  // 3D & Intelligence props
  enable3D?: boolean;
  showConflictZones?: boolean;
  showGlobalBases?: boolean;
  showNuclearSites?: boolean;
  showChokepoints?: boolean;
  // Phase 2: Spectral, SAR, FIRMS, Object Detection
  spectralIndex?: string | null; // null = RGB, 'ndvi' | 'ndwi' | 'ndbi' | 'nbr'
  showFIRMS?: boolean;
  showSAROnly?: boolean;
  objectDetectionResults?: any;
  changeDetectionJob?: any;
  // Phase 4: Entity pins
  entityPins?: Array<{
    entity_id: string;
    name: string;
    entity_type: string;
    lat: number;
    lon: number;
    priority?: number;
    is_watchlisted?: boolean;
    observation_count?: number;
  }>;
  onEntityPinClick?: (entityId: string) => void;
  selectedEntityHighlight?: {
    lat: number; lon: number; entity_type: string; name: string;
  } | null;
  // Live aircraft/vessel showcase overlay — shown around the current view without
  // requiring an AOI to be selected (proves the tracking capability on page load).
  // See GEOINT_Wayback.tsx's on-map-ready fetch.
  liveAircraft?: Array<{ id: string; label?: string | null; type?: string | null; lat: number; lon: number; track?: number | null }>;
  liveVessels?: Array<{ id: string; label?: string | null; type?: string | null; lat: number; lon: number; course?: number | null }>;
  onMapReady?: () => void;
  onLiveAircraftClick?: (id: string) => void;
  onLiveVesselClick?: (id: string) => void;
  // Selected flight's route — origin/destination airport resolved offline (see
  // services/airport_lookup.py) plus the aircraft's current position. Rendered as a
  // line (flown leg solid, remaining leg dashed) with airport markers at the ends.
  selectedFlightPath?: {
    origin?: { lat: number; lon: number; name?: string | null } | null;
    current: { lat: number; lon: number };
    destination?: { lat: number; lon: number; name?: string | null } | null;
  } | null;
}

const WaybackMap = forwardRef<maplibregl.Map, WaybackMapProps>(
  ({ activeReleaseId, selectedLocation, showLabels, showMilitaryBases = false, militaryBases = [], drawingMode = null, onAOIDraw, drawnAOI = null, savedAOIs = [], selectedAOIId = null, releases = [], enable3D = false, showConflictZones = false, showGlobalBases = false, showNuclearSites = false, showChokepoints = false, spectralIndex = null, showFIRMS = false, showSAROnly = false, objectDetectionResults = null, changeDetectionJob = null, entityPins = [], onEntityPinClick, selectedEntityHighlight = null, liveAircraft = [], liveVessels = [], onMapReady, onLiveAircraftClick, onLiveVesselClick, selectedFlightPath = null }, ref) => {
    const [changeFootprints, setChangeFootprints] = useState<Array<{ id: string | number; geometry: any }>>([]);
    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<maplibregl.Map | null>(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    // Stable ref for click handler — avoids re-triggering entity pins useEffect on every render
    const onEntityPinClickRef = useRef(onEntityPinClick);
    useEffect(() => { onEntityPinClickRef.current = onEntityPinClick; }, [onEntityPinClick]);
    const onMapReadyRef = useRef(onMapReady);
    useEffect(() => { onMapReadyRef.current = onMapReady; }, [onMapReady]);
    const onLiveAircraftClickRef = useRef(onLiveAircraftClick);
    useEffect(() => { onLiveAircraftClickRef.current = onLiveAircraftClick; }, [onLiveAircraftClick]);
    const onLiveVesselClickRef = useRef(onLiveVesselClick);
    useEffect(() => { onLiveVesselClickRef.current = onLiveVesselClick; }, [onLiveVesselClick]);
    const detectionPopupRef = useRef<maplibregl.Popup | null>(null);
    const entityHighlightMarkerRef = useRef<maplibregl.Marker | null>(null);

    // Activity counts from current detection results (shown in map overlay panel)
    const activityCounts = React.useMemo(() => {
      if (!objectDetectionResults?.tile_results) return null;
      const counts: Record<string, number> = {};
      for (const tr of objectDetectionResults.tile_results) {
        for (const det of (tr.detections || [])) {
          const cls = det.class_name || 'unknown';
          counts[cls] = (counts[cls] || 0) + 1;
        }
      }
      return Object.keys(counts).length > 0 ? counts : null;
    }, [objectDetectionResults]);
    const drawingStateRef = useRef<{
      mode: 'rectangle' | 'polygon' | null;
      startPoint: [number, number] | null;
      polygonPoints: [number, number][];
      rectangle: any;
      polygon: any;
    }>({
      mode: null,
      startPoint: null,
      polygonPoints: [],
      rectangle: null,
      polygon: null
    });

    // Initialize map
    useEffect(() => {
      if (mapContainer.current && !map.current) {
        map.current = new maplibregl.Map({
          container: mapContainer.current,
          style: {
            version: 8,
            glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
            sources: {
              'esri-world-imagery': {
                type: 'raster',
                tiles: [
                  'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
                ],
                tileSize: 256,
                attribution: 'Sycek Intelligence Geoint'
              },
              'india-topomap': {
                type: 'raster',
                tiles: [
                  'https://services.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}'
                ],
                tileSize: 256,
                attribution: 'Sycek Intelligence Geoint - Topographic Data'
              },
              'india-boundaries': {
                type: 'raster',
                tiles: [
                  'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'
                ],
                tileSize: 256,
                attribution: 'Sycek Intelligence Geoint'
              },
              // Terrain & buildings added dynamically when 3D mode enabled
            },
            layers: [
              {
                id: 'esri-world-imagery',
                type: 'raster',
                source: 'esri-world-imagery',
                paint: { 'raster-opacity': 1 }
              },
              {
                id: 'india-topomap',
                type: 'raster',
                source: 'india-topomap',
                paint: { 'raster-opacity': 0.3 },
                minzoom: 0,
                maxzoom: 19
              },
              {
                id: 'india-boundaries',
                type: 'raster',
                source: 'india-boundaries',
                paint: { 'raster-opacity': 0.4 },
                minzoom: 0,
                maxzoom: 19
              }
            ],
          },
          center: [77.6915, 13.1865], // Default to Bangalore
          zoom: 12,
          pitch: enable3D ? 45 : 0,
          bearing: enable3D ? -17.6 : 0,
          maxPitch: 85,
        });

        map.current.on('load', () => {
          setMapLoaded(true);
          if (ref) {
            (ref as React.MutableRefObject<maplibregl.Map>).current = map.current!;
          }
          onMapReadyRef.current?.();
        });

        // Add navigation controls
        map.current.addControl(new maplibregl.NavigationControl(), 'top-left');
        map.current.addControl(new maplibregl.FullscreenControl(), 'top-left');
      }

      return () => {
        if (map.current) {
          map.current.remove();
          map.current = null;
        }
      };
    }, [ref]);

    // Toggle 3D terrain on/off
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const m = map.current;
      try {
        if (enable3D) {
          // Enable terrain
          if (!m.getSource('terrain-source')) {
            m.addSource('terrain-source', {
              type: 'raster-dem',
              tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
              tileSize: 256,
              encoding: 'terrarium',
              maxzoom: 15,
            });
          }
          (m as any).setTerrain({ source: 'terrain-source', exaggeration: 1.5 });
          m.easeTo({ pitch: 50, bearing: -17.6, duration: 1000 });

          // Add 3D building extrusions from OSM via Overture/OpenFreeMap
          if (!m.getSource('osm-buildings')) {
            m.addSource('osm-buildings', {
              type: 'vector',
              tiles: ['https://tiles.openfreemap.org/planet/{z}/{x}/{y}.pbf'],
              maxzoom: 14,
            });
          }
          if (!m.getLayer('3d-buildings')) {
            m.addLayer({
              id: '3d-buildings',
              source: 'osm-buildings',
              'source-layer': 'building',
              type: 'fill-extrusion',
              minzoom: 13,
              paint: {
                'fill-extrusion-color': [
                  'interpolate', ['linear'], ['get', 'render_height'],
                  0, '#1e293b',
                  50, '#334155',
                  200, '#475569',
                ],
                'fill-extrusion-height': ['coalesce', ['get', 'render_height'], 5],
                'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
                'fill-extrusion-opacity': 0.7,
              },
            } as any);
          }
        } else {
          // Disable terrain
          (m as any).setTerrain(null);
          m.easeTo({ pitch: 0, bearing: 0, duration: 800 });
          if (m.getLayer('3d-buildings')) m.removeLayer('3d-buildings');
        }
      } catch (e) {
        console.warn('3D terrain toggle error:', e);
      }
    }, [enable3D, mapLoaded]);

    // Intelligence overlays: Global military bases
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const m = map.current;
      const srcId = 'intel-global-bases-src';
      const layerId = 'intel-global-bases-lyr';
      const labelId = 'intel-global-bases-labels';

      // Clean up
      [labelId, layerId].forEach(id => { if (m.getLayer(id)) m.removeLayer(id); });
      if (m.getSource(srcId)) m.removeSource(srcId);

      if (!showGlobalBases) return;

      const features = GLOBAL_MILITARY_BASES.filter(b => b.category === 'military').map(b => ({
        type: 'Feature' as const,
        properties: { name: b.name, type: b.type, country: b.country, info: b.info, threat_level: b.threat_level || 'medium' },
        geometry: { type: 'Point' as const, coordinates: b.coordinates },
      }));

      m.addSource(srcId, { type: 'geojson', data: { type: 'FeatureCollection', features } });

      // Build color stops from CATEGORY_COLORS
      const colorExpr: any[] = ['case'];
      Object.entries(CATEGORY_COLORS).forEach(([country, color]) => {
        colorExpr.push(['==', ['get', 'country'], country], color);
      });
      colorExpr.push('#64748b');

      m.addLayer({
        id: layerId, type: 'circle', source: srcId,
        paint: {
          'circle-color': colorExpr as any,
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 4, 6, 7, 10, 10],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.9,
        },
      });
      m.addLayer({
        id: labelId, type: 'symbol', source: srcId,
        minzoom: 5,
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-size': 10, 'text-offset': [0, 1.5], 'text-anchor': 'top',
          'text-max-width': 10,
        },
        paint: { 'text-color': '#e2e8f0', 'text-halo-color': '#000000', 'text-halo-width': 1.5 },
      });

      // Popups
      m.on('click', layerId, (e: any) => {
        if (e.features?.length) {
          const p = e.features[0].properties;
          new maplibregl.Popup({ className: 'intel-popup' })
            .setLngLat(e.lngLat)
            .setHTML(`<div style="padding:10px;max-width:280px;"><h3 style="margin:0 0 6px;font-size:15px;font-weight:700;color:#f1f5f9;">${p.name}</h3><div style="color:#94a3b8;font-size:12px;margin-bottom:4px;"><strong>${p.country}</strong> &bull; ${p.type}</div><p style="color:#cbd5e1;font-size:13px;margin:6px 0 0;">${p.info}</p></div>`)
            .addTo(m);
        }
      });
      m.on('mouseenter', layerId, () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', layerId, () => { m.getCanvas().style.cursor = ''; });
    }, [showGlobalBases, mapLoaded]);

    // Intelligence overlays: Nuclear sites
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const m = map.current;
      const srcId = 'intel-nuclear-src';
      const layerId = 'intel-nuclear-lyr';
      const pulseId = 'intel-nuclear-pulse';
      const labelId = 'intel-nuclear-labels';

      [labelId, pulseId, layerId].forEach(id => { if (m.getLayer(id)) m.removeLayer(id); });
      if (m.getSource(srcId)) m.removeSource(srcId);

      if (!showNuclearSites) return;

      const features = GLOBAL_MILITARY_BASES.filter(b => b.category === 'nuclear').map(b => ({
        type: 'Feature' as const,
        properties: { name: b.name, country: b.country, info: b.info },
        geometry: { type: 'Point' as const, coordinates: b.coordinates },
      }));

      m.addSource(srcId, { type: 'geojson', data: { type: 'FeatureCollection', features } });
      // Outer pulse ring
      m.addLayer({
        id: pulseId, type: 'circle', source: srcId,
        paint: { 'circle-color': '#fbbf24', 'circle-radius': 16, 'circle-opacity': 0.2, 'circle-stroke-width': 0 },
      });
      // Core dot
      m.addLayer({
        id: layerId, type: 'circle', source: srcId,
        paint: { 'circle-color': '#fbbf24', 'circle-radius': 7, 'circle-stroke-color': '#ef4444', 'circle-stroke-width': 2.5 },
      });
      m.addLayer({
        id: labelId, type: 'symbol', source: srcId,
        layout: { 'text-field': ['concat', '☢ ', ['get', 'name']], 'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'], 'text-size': 11, 'text-offset': [0, 1.8], 'text-anchor': 'top' },
        paint: { 'text-color': '#fbbf24', 'text-halo-color': '#000', 'text-halo-width': 2 },
      });

      m.on('click', layerId, (e: any) => {
        if (e.features?.length) {
          const p = e.features[0].properties;
          new maplibregl.Popup({ className: 'intel-popup' })
            .setLngLat(e.lngLat)
            .setHTML(`<div style="padding:10px;max-width:280px;background:#1c1917;border:1px solid #fbbf24;border-radius:6px;"><h3 style="margin:0 0 6px;font-size:15px;font-weight:700;color:#fbbf24;">☢ ${p.name}</h3><div style="color:#94a3b8;font-size:12px;margin-bottom:4px;">${p.country}</div><p style="color:#cbd5e1;font-size:13px;margin:6px 0 0;">${p.info}</p></div>`)
            .addTo(m);
        }
      });
      m.on('mouseenter', layerId, () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', layerId, () => { m.getCanvas().style.cursor = ''; });
    }, [showNuclearSites, mapLoaded]);

    // Intelligence overlays: Chokepoints
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const m = map.current;
      const srcId = 'intel-chokepoints-src';
      const layerId = 'intel-chokepoints-lyr';
      const labelId = 'intel-chokepoints-labels';

      [labelId, layerId].forEach(id => { if (m.getLayer(id)) m.removeLayer(id); });
      if (m.getSource(srcId)) m.removeSource(srcId);

      if (!showChokepoints) return;

      const features = GLOBAL_MILITARY_BASES.filter(b => b.category === 'chokepoint').map(b => ({
        type: 'Feature' as const,
        properties: { name: b.name, info: b.info, threat_level: b.threat_level || 'high' },
        geometry: { type: 'Point' as const, coordinates: b.coordinates },
      }));

      m.addSource(srcId, { type: 'geojson', data: { type: 'FeatureCollection', features } });
      m.addLayer({
        id: layerId, type: 'circle', source: srcId,
        paint: {
          'circle-color': 'rgba(239, 68, 68, 0.15)',
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 20, 6, 40, 10, 60],
          'circle-stroke-color': '#ef4444',
          'circle-stroke-width': 2,
          'circle-stroke-opacity': 0.7,
        },
      });
      m.addLayer({
        id: labelId, type: 'symbol', source: srcId,
        layout: { 'text-field': ['get', 'name'], 'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'], 'text-size': 12, 'text-anchor': 'center' },
        paint: { 'text-color': '#fca5a5', 'text-halo-color': '#000', 'text-halo-width': 2 },
      });

      m.on('click', layerId, (e: any) => {
        if (e.features?.length) {
          const p = e.features[0].properties;
          new maplibregl.Popup({ className: 'intel-popup' }).setLngLat(e.lngLat)
            .setHTML(`<div style="padding:10px;max-width:260px;"><h3 style="margin:0 0 6px;font-size:14px;font-weight:700;color:#fca5a5;">${p.name}</h3><p style="color:#cbd5e1;font-size:13px;margin:0;">${p.info}</p></div>`)
            .addTo(m);
        }
      });
      m.on('mouseenter', layerId, () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', layerId, () => { m.getCanvas().style.cursor = ''; });
    }, [showChokepoints, mapLoaded]);

    // Intelligence overlays: Conflict zones (polygons)
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const m = map.current;
      const srcId = 'intel-conflicts-src';
      const fillId = 'intel-conflicts-fill';
      const lineId = 'intel-conflicts-line';
      const labelId = 'intel-conflicts-labels';

      [labelId, lineId, fillId].forEach(id => { if (m.getLayer(id)) m.removeLayer(id); });
      if (m.getSource(srcId)) m.removeSource(srcId);

      if (!showConflictZones) return;

      const features = CONFLICT_ZONES.map(z => ({
        type: 'Feature' as const,
        properties: {
          name: z.name, status: z.status, description: z.description,
          parties: z.parties.join(', '),
          fill_color: CONFLICT_STATUS_COLORS[z.status] || 'rgba(239,68,68,0.2)',
          border_color: CONFLICT_BORDER_COLORS[z.status] || '#ef4444',
        },
        geometry: { type: 'Polygon' as const, coordinates: [z.coordinates] },
      }));

      m.addSource(srcId, { type: 'geojson', data: { type: 'FeatureCollection', features } });
      m.addLayer({
        id: fillId, type: 'fill', source: srcId,
        paint: { 'fill-color': ['get', 'fill_color'], 'fill-opacity': 0.6 },
      });
      m.addLayer({
        id: lineId, type: 'line', source: srcId,
        paint: { 'line-color': ['get', 'border_color'], 'line-width': 2, 'line-dasharray': [4, 2] },
      });
      // Centroid labels
      const labelFeatures = CONFLICT_ZONES.map(z => {
        const lngs = z.coordinates.map(c => c[0]);
        const lats = z.coordinates.map(c => c[1]);
        const cLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
        const cLat = lats.reduce((a, b) => a + b, 0) / lats.length;
        return {
          type: 'Feature' as const,
          properties: { name: z.name, status: z.status.toUpperCase() },
          geometry: { type: 'Point' as const, coordinates: [cLng, cLat] },
        };
      });
      const labelSrc = srcId + '-labels';
      if (m.getSource(labelSrc)) m.removeSource(labelSrc);
      m.addSource(labelSrc, { type: 'geojson', data: { type: 'FeatureCollection', features: labelFeatures } });
      m.addLayer({
        id: labelId, type: 'symbol', source: labelSrc,
        layout: {
          'text-field': ['concat', ['get', 'name'], '\n[', ['get', 'status'], ']'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 12, 'text-anchor': 'center', 'text-justify': 'center',
        },
        paint: { 'text-color': '#fca5a5', 'text-halo-color': 'rgba(0,0,0,0.8)', 'text-halo-width': 2 },
      });

      m.on('click', fillId, (e: any) => {
        if (e.features?.length) {
          const p = e.features[0].properties;
          new maplibregl.Popup({ className: 'intel-popup' }).setLngLat(e.lngLat)
            .setHTML(`<div style="padding:10px;max-width:300px;"><h3 style="margin:0 0 6px;font-size:15px;font-weight:700;color:#fca5a5;">${p.name}</h3><div style="color:#94a3b8;font-size:11px;margin-bottom:6px;">Status: <strong>${p.status.toUpperCase()}</strong></div><p style="color:#cbd5e1;font-size:13px;margin:0 0 6px;">${p.description}</p><div style="color:#94a3b8;font-size:11px;">Parties: ${p.parties}</div></div>`)
            .addTo(m);
        }
      });
    }, [showConflictZones, mapLoaded]);

    // Update wayback release layer
    useEffect(() => {
      if (map.current && mapLoaded) {
        const release = releases.find(r => r.id === activeReleaseId);
        setWaybackRelease(map.current, activeReleaseId, release);
        
        // Update change footprints for active release
        if (release && release.hasLocalChanges && release.footprint && activeReleaseId !== null) {
          setChangeFootprints([{ id: activeReleaseId, geometry: release.footprint }]);
        } else {
          setChangeFootprints([]);
        }
      }
    }, [activeReleaseId, mapLoaded, releases]);
    
    // Display change footprints on map
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      
      const mapInstance = map.current;
      const srcId = 'change-footprints-src';
      const layerId = 'change-footprints-lyr';
      
      // Remove all layers using this source before removing the source itself
      const outlineLayerId = `${layerId}-outline`;
      try {
        if (mapInstance.getLayer(outlineLayerId)) mapInstance.removeLayer(outlineLayerId);
        if (mapInstance.getLayer(layerId)) mapInstance.removeLayer(layerId);
        if (mapInstance.getSource(srcId)) mapInstance.removeSource(srcId);
      } catch (cleanupErr) {
        // Ignore stale-layer errors during cleanup
        console.warn('change-footprints cleanup warning:', cleanupErr);
      }

      // Add change footprints if available
      if (changeFootprints.length > 0) {
        const features = changeFootprints.map(fp => ({
          type: 'Feature' as const,
          geometry: fp.geometry,
          properties: {
            id: fp.id,
            type: 'change_area'
          }
        }));
        
        mapInstance.addSource(srcId, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features
          }
        });
        
        mapInstance.addLayer({
          id: layerId,
          type: 'fill',
          source: srcId,
          paint: {
            'fill-color': '#ff6b6b',
            'fill-opacity': 0.3
          }
        });
        
        // Add outline layer for better visibility
        mapInstance.addLayer({
          id: `${layerId}-outline`,
          type: 'line',
          source: srcId,
          paint: {
            'line-color': '#ff0000',
            'line-width': 2,
            'line-opacity': 0.8
          }
        });
        
        console.log('Displayed change footprints:', changeFootprints.length);
      }
    }, [changeFootprints, mapLoaded]);

    // Center map on selected location
    useEffect(() => {
      if (map.current && mapLoaded && selectedLocation) {
        map.current.flyTo({
          center: selectedLocation.coordinates,
          zoom: 12,
          duration: 1000
        });
      }
    }, [selectedLocation, mapLoaded]);

    // Update labels visibility
    useEffect(() => {
      if (map.current && mapLoaded) {
        // Toggle reference labels
        // This would require adding a labels source and layer
        console.log('Labels visibility:', showLabels);
      }
    }, [showLabels, mapLoaded]);

    const updateLocationMarker = useCallback((mapInstance: maplibregl.Map, location: any) => {
      const srcId = 'location-src';
      const layerId = 'location-lyr';

      // Remove existing location marker
      if (mapInstance.getLayer(`${layerId}-label`)) {
        mapInstance.removeLayer(`${layerId}-label`);
      }
      if (mapInstance.getLayer(layerId)) {
        mapInstance.removeLayer(layerId);
      }
      if (mapInstance.getSource(srcId)) {
        mapInstance.removeSource(srcId);
      }

      if (location) {
        // Add location marker
        mapInstance.addSource(srcId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: location.coordinates
            },
            properties: {
              name: location.name
            }
          }
        });

        mapInstance.addLayer({
          id: layerId,
          type: 'circle',
          source: srcId,
          paint: {
            'circle-color': '#ef4444',
            'circle-radius': 8,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2
          }
        });

        mapInstance.addLayer({
          id: `${layerId}-label`,
          type: 'symbol',
          source: srcId,
          layout: {
            'text-field': ['get', 'name'],
            'text-offset': [0, -2],
            'text-anchor': 'bottom',
            'text-size': 12
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': '#000000',
            'text-halo-width': 2
          }
        });
      }
    }, []);

    // Update selected location marker
    useEffect(() => {
      if (map.current && mapLoaded && selectedLocation) {
        updateLocationMarker(map.current, selectedLocation);
      }
    }, [selectedLocation, mapLoaded, updateLocationMarker]);

    // Update military bases
    useEffect(() => {
      if (map.current && mapLoaded) {
        updateMilitaryBases(map.current, militaryBases, showMilitaryBases);
      }
    }, [showMilitaryBases, militaryBases, mapLoaded]);

    // Display drawn AOI on map
    useEffect(() => {
      if (!map.current || !mapLoaded) return;

      const mapInstance = map.current;
      const aoiSourceId = 'aoi-display-src';
      const aoiLayerId = 'aoi-display-layer';
      const aoiOutlineId = 'aoi-display-outline';

      // Clean up temporary drawing rectangle when permanent AOI is set
      if (drawnAOI && drawnAOI.coordinates && drawnAOI.coordinates.length > 0) {
        // Remove temporary drawing layers
        if (mapInstance.getLayer('drawing-rectangle-outline')) {
          mapInstance.removeLayer('drawing-rectangle-outline');
        }
        if (mapInstance.getLayer('drawing-rectangle')) {
          mapInstance.removeLayer('drawing-rectangle');
        }
        if (mapInstance.getSource('drawing-rectangle')) {
          mapInstance.removeSource('drawing-rectangle');
        }
        if (mapInstance.getLayer('drawing-polygon-outline')) {
          mapInstance.removeLayer('drawing-polygon-outline');
        }
        if (mapInstance.getLayer('drawing-polygon')) {
          mapInstance.removeLayer('drawing-polygon');
        }
        if (mapInstance.getSource('drawing-polygon')) {
          mapInstance.removeSource('drawing-polygon');
        }
      }

      // Remove existing AOI display
      if (mapInstance.getLayer(aoiOutlineId)) {
        mapInstance.removeLayer(aoiOutlineId);
      }
      if (mapInstance.getLayer(aoiLayerId)) {
        mapInstance.removeLayer(aoiLayerId);
      }
      if (mapInstance.getSource(aoiSourceId)) {
        mapInstance.removeSource(aoiSourceId);
      }

      // Add new AOI if provided
      if (drawnAOI && drawnAOI.coordinates && drawnAOI.coordinates.length > 0) {
        const aoiGeoJSON = {
          type: 'Feature' as const,
          geometry: {
            type: drawnAOI.type as 'Polygon',
            coordinates: drawnAOI.coordinates
          },
          properties: {}
        };

        mapInstance.addSource(aoiSourceId, {
          type: 'geojson',
          data: aoiGeoJSON
        });

        mapInstance.addLayer({
          id: aoiLayerId,
          type: 'fill',
          source: aoiSourceId,
          paint: {
            'fill-color': '#3b82f6',
            'fill-opacity': 0.3
          }
        });

        mapInstance.addLayer({
          id: aoiOutlineId,
          type: 'line',
          source: aoiSourceId,
          paint: {
            'line-color': '#3b82f6',
            'line-width': 3,
            'line-dasharray': [2, 2]
          }
        });

        console.log('AOI displayed on map:', drawnAOI);
      }
    }, [drawnAOI, mapLoaded]);

    // Display saved AOIs on map
    useEffect(() => {
      if (!map.current || !mapLoaded) return;

      const mapInstance = map.current;
      const savedAOIsSourceId = 'saved-aois-src';
      const savedAOIsLayerId = 'saved-aois-layer';
      const savedAOIsOutlineId = 'saved-aois-outline';

      // Remove existing saved AOIs
      if (mapInstance.getLayer(savedAOIsOutlineId)) {
        mapInstance.removeLayer(savedAOIsOutlineId);
      }
      if (mapInstance.getLayer(savedAOIsLayerId)) {
        mapInstance.removeLayer(savedAOIsLayerId);
      }
      if (mapInstance.getSource(savedAOIsSourceId)) {
        mapInstance.removeSource(savedAOIsSourceId);
      }

      // Add saved AOIs if provided
      if (savedAOIs && savedAOIs.length > 0) {
        console.log('Rendering saved AOIs on map:', savedAOIs.length);
        const features = savedAOIs.map((aoi: any) => {
          // Handle both coordinate formats
          let coords: number[][][] = aoi.geometry?.coordinates;
          if (!coords) {
            console.warn('AOI missing coordinates:', aoi);
            return null;
          }
          // Ensure coordinates are in the right format (number[][][])
          if (Array.isArray(coords[0]) && !Array.isArray(coords[0][0])) {
            // If it's number[][], wrap it in another array
            coords = [coords as unknown as number[][]];
          }
          const aoiId = aoi.id || aoi.aoi_id || '';
          const mlScore: number = (aoi as any).metadata?.ml_score ?? -1;
          return {
            type: 'Feature' as const,
            geometry: {
              type: (aoi.geometry?.type || 'Polygon') as 'Polygon',
              coordinates: coords as number[][][]
            },
            properties: {
              id: aoiId,
              name: aoi.name || '',
              selected: aoiId === selectedAOIId,
              ml_score: mlScore
            }
          };
        }).filter(Boolean) as Array<{
          type: 'Feature';
          geometry: { type: 'Polygon'; coordinates: number[][][] };
          properties: { id: string; name: string; selected: boolean; ml_score: number };
        }>;

        const aoiGeoJSON = {
          type: 'FeatureCollection' as const,
          features
        };

        mapInstance.addSource(savedAOIsSourceId, {
          type: 'geojson',
          data: aoiGeoJSON as any
        });

        // Severity color: selected → white outline; unselected → ml_score-based
        // ml_score: >=0.75 critical (red), >=0.55 high (orange), >=0.35 elevated (yellow), else normal (blue), -1 = no score (blue)
        const severityColor = [
          'case',
          ['==', ['get', 'selected'], true], '#ffffff',
          ['>=', ['get', 'ml_score'], 0.75], '#ef4444',   // critical
          ['>=', ['get', 'ml_score'], 0.55], '#f97316',   // high
          ['>=', ['get', 'ml_score'], 0.35], '#eab308',   // elevated
          '#3b82f6'                                         // normal / no score
        ] as any;

        mapInstance.addLayer({
          id: savedAOIsLayerId,
          type: 'fill',
          source: savedAOIsSourceId,
          paint: {
            'fill-color': severityColor,
            'fill-opacity': [
              'case',
              ['==', ['get', 'selected'], true],
              0.25,
              0.18
            ]
          }
        });

        mapInstance.addLayer({
          id: savedAOIsOutlineId,
          type: 'line',
          source: savedAOIsSourceId,
          paint: {
            'line-color': severityColor,
            'line-width': [
              'case',
              ['==', ['get', 'selected'], true],
              3,
              2
            ],
            'line-dasharray': [2, 2]
          }
        });

        console.log('Saved AOIs displayed on map:', savedAOIs.length);
      }
    }, [savedAOIs, selectedAOIId, mapLoaded]);

    // Handle drawing mode
    useEffect(() => {
      if (!map.current || !mapLoaded) return;

      const mapInstance = map.current;
      const drawingState = drawingStateRef.current;

      // Clean up previous drawing state
      const cleanupDrawing = () => {
        if (drawingState.rectangle) {
          if (mapInstance.getLayer('drawing-rectangle-outline')) {
            mapInstance.removeLayer('drawing-rectangle-outline');
          }
          if (mapInstance.getLayer('drawing-rectangle')) {
            mapInstance.removeLayer('drawing-rectangle');
          }
          if (mapInstance.getSource('drawing-rectangle')) {
            mapInstance.removeSource('drawing-rectangle');
          }
          drawingState.rectangle = null;
        }
        if (drawingState.polygon) {
          if (mapInstance.getLayer('drawing-polygon-outline')) {
            mapInstance.removeLayer('drawing-polygon-outline');
          }
          if (mapInstance.getLayer('drawing-polygon')) {
            mapInstance.removeLayer('drawing-polygon');
          }
          if (mapInstance.getSource('drawing-polygon')) {
            mapInstance.removeSource('drawing-polygon');
          }
          drawingState.polygon = null;
        }
        drawingState.startPoint = null;
        drawingState.polygonPoints = [];
      };

      // Clean up any existing handlers first
      const allHandlers: Array<{ event: string; handler: any }> = [];
      
      // Store handlers for cleanup
      const storedHandlers = {
        mousedown: null as any,
        mousemove: null as any,
        mouseup: null as any,
        dblclick: null as any
      };
      
      if (drawingMode) {
        console.log('Drawing mode activated:', drawingMode);
        drawingState.mode = drawingMode;
        cleanupDrawing();
        mapInstance.getCanvas().style.cursor = 'crosshair';
        mapInstance.getCanvas().style.pointerEvents = 'auto';
        
        // Disable map rotation and dragging when drawing
        mapInstance.dragRotate.disable();
        mapInstance.boxZoom.disable();
        mapInstance.doubleClickZoom.disable();

        // Rectangle drawing
        if (drawingMode === 'rectangle') {
          const handleMouseDown = (e: maplibregl.MapMouseEvent) => {
            e.preventDefault();
            if (e.originalEvent.button !== 0) return; // Only left click
            console.log('Rectangle drawing started at:', e.lngLat);
            drawingState.startPoint = [e.lngLat.lng, e.lngLat.lat];
          };

          const handleMouseMove = (e: maplibregl.MapMouseEvent) => {
            if (!drawingState.startPoint) return;
            console.log('Mouse move during rectangle drawing');
            
            const [startLng, startLat] = drawingState.startPoint;
            const [endLng, endLat] = [e.lngLat.lng, e.lngLat.lat];

            // Remove existing rectangle
            if (mapInstance.getLayer('drawing-rectangle-outline')) {
              mapInstance.removeLayer('drawing-rectangle-outline');
            }
            if (mapInstance.getLayer('drawing-rectangle')) {
              mapInstance.removeLayer('drawing-rectangle');
            }
            if (mapInstance.getSource('drawing-rectangle')) {
              mapInstance.removeSource('drawing-rectangle');
            }

            // Create rectangle GeoJSON
            const rectangleGeoJSON = {
              type: 'Feature' as const,
              geometry: {
                type: 'Polygon' as const,
                coordinates: [[
                  [startLng, startLat],
                  [endLng, startLat],
                  [endLng, endLat],
                  [startLng, endLat],
                  [startLng, startLat]
                ]]
              },
              properties: {}
            };

            // Add rectangle source and layer
            mapInstance.addSource('drawing-rectangle', {
              type: 'geojson',
              data: rectangleGeoJSON
            });

            mapInstance.addLayer({
              id: 'drawing-rectangle',
              type: 'fill',
              source: 'drawing-rectangle',
              paint: {
                'fill-color': '#00ff88',
                'fill-opacity': 0.2
              }
            });

            mapInstance.addLayer({
              id: 'drawing-rectangle-outline',
              type: 'line',
              source: 'drawing-rectangle',
              paint: {
                'line-color': '#00ff88',
                'line-width': 2
              }
            });

            drawingState.rectangle = rectangleGeoJSON;
          };

          const handleMouseUp = (e: maplibregl.MapMouseEvent) => {
            e.preventDefault();
            if (e.originalEvent && e.originalEvent.stopPropagation) {
              e.originalEvent.stopPropagation();
            }
            console.log('Mouse up event fired', drawingState.startPoint);
            if (!drawingState.startPoint) {
              console.log('No start point, ignoring mouseup');
              return;
            }
            
            const [startLng, startLat] = drawingState.startPoint;
            const [endLng, endLat] = [e.lngLat.lng, e.lngLat.lat];

            console.log('Rectangle end point:', { startLng, startLat, endLng, endLat });

            // Only create if there's actual movement
            const minDistance = 0.0001; // Minimum distance in degrees
            const distanceLng = Math.abs(endLng - startLng);
            const distanceLat = Math.abs(endLat - startLat);
            
            if (distanceLng < minDistance && distanceLat < minDistance) {
              console.log('Distance too small, ignoring:', { distanceLng, distanceLat });
              drawingState.startPoint = null;
              return;
            }

            // Finalize rectangle
            const coordinates: number[][][] = [[
              [startLng, startLat],
              [endLng, startLat],
              [endLng, endLat],
              [startLng, endLat],
              [startLng, startLat]
            ]];

            console.log('Rectangle finalized, coordinates:', coordinates);
            console.log('Calling onAOIDraw callback:', !!onAOIDraw);

            // Keep the rectangle visible - don't clean it up yet
            // Just update the drawing state
            drawingState.mode = null;
            drawingState.startPoint = null;
            mapInstance.getCanvas().style.cursor = '';
            
            // Re-enable map controls
            mapInstance.dragRotate.enable();
            mapInstance.boxZoom.enable();
            mapInstance.doubleClickZoom.enable();

            // Call the callback to notify parent component
            if (onAOIDraw) {
              try {
                onAOIDraw({
                  type: 'Polygon',
                  coordinates
                });
                console.log('onAOIDraw callback executed successfully');
              } catch (error) {
                console.error('Error in onAOIDraw callback:', error);
              }
            } else {
              console.warn('onAOIDraw callback is not defined!');
            }
          };

          storedHandlers.mousedown = handleMouseDown;
          storedHandlers.mousemove = handleMouseMove;
          storedHandlers.mouseup = handleMouseUp;
          
          mapInstance.on('mousedown', handleMouseDown);
          mapInstance.on('mousemove', handleMouseMove);
          mapInstance.on('mouseup', handleMouseUp);
          
          allHandlers.push(
            { event: 'mousedown', handler: handleMouseDown },
            { event: 'mousemove', handler: handleMouseMove },
            { event: 'mouseup', handler: handleMouseUp }
          );
        }

        // Polygon drawing
        if (drawingMode === 'polygon') {
          const updatePolygonLayer = () => {
            if (drawingState.polygonPoints.length < 2) return;

            // Remove existing polygon
            if (mapInstance.getLayer('drawing-polygon-outline')) {
              mapInstance.removeLayer('drawing-polygon-outline');
            }
            if (mapInstance.getLayer('drawing-polygon')) {
              mapInstance.removeLayer('drawing-polygon');
            }
            if (mapInstance.getSource('drawing-polygon')) {
              mapInstance.removeSource('drawing-polygon');
            }

            // Create polygon GeoJSON (close it temporarily for display)
            const points = [...drawingState.polygonPoints];
            if (points.length >= 2 && (points[0][0] !== points[points.length - 1][0] || points[0][1] !== points[points.length - 1][1])) {
              points.push(points[0]);
            }

            const polygonGeoJSON = {
              type: 'Feature' as const,
              geometry: {
                type: 'Polygon' as const,
                coordinates: [points]
              },
              properties: {}
            };

            // Add polygon source and layer
            mapInstance.addSource('drawing-polygon', {
              type: 'geojson',
              data: polygonGeoJSON
            });

            mapInstance.addLayer({
              id: 'drawing-polygon',
              type: 'fill',
              source: 'drawing-polygon',
              paint: {
                'fill-color': '#00ff88',
                'fill-opacity': 0.2
              }
            });

            mapInstance.addLayer({
              id: 'drawing-polygon-outline',
              type: 'line',
              source: 'drawing-polygon',
              paint: {
                'line-color': '#00ff88',
                'line-width': 2
              }
            });

            drawingState.polygon = polygonGeoJSON;
          };

          const handleMouseDown = (e: maplibregl.MapMouseEvent) => {
            e.preventDefault();
            if (e.originalEvent.button !== 0) return;
            console.log('Polygon point added at:', e.lngLat);
            drawingState.polygonPoints.push([e.lngLat.lng, e.lngLat.lat]);
            updatePolygonLayer();
          };

          const handleDoubleClick = (e: maplibregl.MapMouseEvent) => {
            e.preventDefault();
            if (drawingState.polygonPoints.length >= 3) {
              // Close polygon
              drawingState.polygonPoints.push(drawingState.polygonPoints[0]);
              
              const coordinates: number[][][] = [drawingState.polygonPoints];
              
              console.log('Polygon drawn:', coordinates);
              
              if (onAOIDraw) {
                onAOIDraw({
                  type: 'Polygon',
                  coordinates
                });
              }

              cleanupDrawing();
              drawingState.mode = null;
              drawingState.polygonPoints = [];
              mapInstance.getCanvas().style.cursor = '';
            }
          };

          storedHandlers.mousedown = handleMouseDown;
          storedHandlers.dblclick = handleDoubleClick;
          
          mapInstance.on('mousedown', handleMouseDown);
          mapInstance.on('dblclick', handleDoubleClick);
          
          allHandlers.push(
            { event: 'mousedown', handler: handleMouseDown },
            { event: 'dblclick', handler: handleDoubleClick }
          );
        }
      } else {
        cleanupDrawing();
        drawingState.mode = null;
        if (mapInstance) {
          mapInstance.getCanvas().style.cursor = '';
          // Re-enable map rotation and controls
          mapInstance.dragRotate.enable();
          mapInstance.boxZoom.enable();
          mapInstance.doubleClickZoom.enable();
        }
      }

      return () => {
        if (mapInstance) {
          // Remove all handlers
          allHandlers.forEach(({ event, handler }) => {
            mapInstance.off(event, handler);
          });
          cleanupDrawing();
          mapInstance.getCanvas().style.cursor = '';
          // Re-enable map rotation and controls
          mapInstance.dragRotate.enable();
          mapInstance.boxZoom.enable();
          mapInstance.doubleClickZoom.enable();
        }
      };
    }, [drawingMode, mapLoaded, onAOIDraw]);

    const setWaybackRelease = (mapInstance: maplibregl.Map, releaseId: string | number | null, release?: { tileTemplate?: string; title?: string }) => {
      const srcId = 'wayback-release-src';
      const layerId = 'wayback-release-lyr';

      console.log('Setting wayback release:', releaseId, 'Release data:', release);

      if (releaseId == null) {
        if (mapInstance.getLayer(layerId)) {
          mapInstance.setLayoutProperty(layerId, 'visibility', 'none');
        }
        return;
      }

      // Determine tile source based on release data
      let tiles: string[];
      
      // First, try to use tileTemplate from release data
      if (release?.tileTemplate) {
        tiles = [release.tileTemplate];
        console.log('Using tileTemplate from release:', tiles);
      } else {
        // Check if this is an EOSDA release (starts with "eosda_")
        if (typeof releaseId === 'string' && releaseId.startsWith('eosda_')) {
          // EOSDA releases should have tileTemplate, but fallback to view_id if available
          const viewId = (release as any)?.view_id;
          if (viewId) {
            // Use RGB composite for Sentinel-2, or default for Landsat
            const dataset = releaseId.includes('sentinel2') ? 'rgb' : '';
            tiles = [`/api/tiles/eosda/${viewId}${dataset ? '/' + dataset : ''}/{z}/{x}/{y}`];
          } else {
            // Fallback: use the release ID directly
            tiles = [`/api/tiles/eosda/${releaseId}/{z}/{x}/{y}`];
          }
        } else {
          // Fallback: Check if this is a high-res basemap release (based on title or ID pattern)
          const releaseIdNum = typeof releaseId === 'string' ? parseInt(releaseId) : releaseId;
          const title = release?.title || '';
          
          // Check title for basemap indicators
          if (title.includes('Vivid Advanced') || title.includes('vivid-advanced')) {
            tiles = [`/api/tiles/wayback/basemap/vivid-advanced/{z}/{x}/{y}.jpg`];
          } else if (title.includes('Vivid Standard') || title.includes('vivid-standard')) {
            tiles = [`/api/tiles/wayback/basemap/vivid-standard/{z}/{x}/{y}.jpg`];
          } else if (title.includes('Community') || title.includes('community')) {
            tiles = [`/api/tiles/wayback/basemap/community/{z}/{x}/{y}.jpg`];
          } else if (releaseIdNum > 1000000) {
            // High-res basemaps typically have higher IDs
            tiles = [`/api/tiles/wayback/basemap/vivid-advanced/{z}/{x}/{y}.jpg`];
          } else {
            // Traditional Wayback release
            tiles = [`/api/tiles/wayback/${releaseId}/{z}/{x}/{y}.jpg`];
          }
        }
        console.log('Using fallback tiles:', tiles);
      }

      // Remove existing source and layer if they exist
      // Must remove layer before source
      if (mapInstance.getLayer(layerId)) {
        mapInstance.removeLayer(layerId);
      }
      if (mapInstance.getSource(srcId)) {
        mapInstance.removeSource(srcId);
      }

      // Add new source and layer immediately
      try {
        mapInstance.addSource(srcId, {
          type: 'raster',
          tiles,
          tileSize: 256
        });
        mapInstance.addLayer({
          id: layerId,
          type: 'raster',
          source: srcId,
          paint: { 'raster-opacity': 1 },
          minzoom: 0,
          maxzoom: 22
        });
        console.log('Successfully added wayback release layer with tiles:', tiles);
      } catch (error) {
        console.error('Error adding wayback release layer:', error);
      }
    };

    const updateMilitaryBases = (mapInstance: maplibregl.Map, bases: MilitaryBase[], show: boolean) => {
      const srcId = 'military-bases-src';
      const layerId = 'military-bases-lyr';

      // Remove existing military bases layer
      if (mapInstance.getLayer(layerId)) {
        mapInstance.removeLayer(layerId);
      }
      if (mapInstance.getSource(srcId)) {
        mapInstance.removeSource(srcId);
      }

      if (show && bases.length > 0) {
        // Create GeoJSON for military bases
        const geojson = {
          type: 'FeatureCollection' as const,
          features: bases.map(base => ({
            type: 'Feature' as const,
            properties: {
              id: base.id,
              name: base.name,
              type: base.type,
              country: base.country,
              info: base.info
            },
            geometry: {
              type: 'Point' as const,
              coordinates: base.coordinates
            }
          }))
        };

        // Add military bases as a source
        mapInstance.addSource(srcId, {
          type: 'geojson',
          data: geojson
        });

        // Add military bases layer with country-specific colors
        mapInstance.addLayer({
          id: layerId,
          type: 'circle',
          source: srcId,
          paint: {
            'circle-color': ['case',
              ['==', ['get', 'country'], 'India'], '#22c55e',      // Green for India
              ['==', ['get', 'country'], 'China'], '#dc2626',      // Red for China
              ['==', ['get', 'country'], 'Pakistan'], '#f59e0b',  // Orange for Pakistan
              '#6b7280'  // Gray for others
            ],
            'circle-radius': ['case',
              ['==', ['get', 'type'], 'Army'], 10,
              ['==', ['get', 'type'], 'Air Force'], 12,
              ['==', ['get', 'type'], 'Navy'], 8,
              8
            ],
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2
          }
        });

        // Add country labels
        mapInstance.addLayer({
          id: `${layerId}-country-labels`,
          type: 'symbol',
          source: srcId,
          layout: {
            'text-field': ['get', 'country'],
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-offset': [0, -2],
            'text-anchor': 'bottom',
            'text-size': 10
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': '#000000',
            'text-halo-width': 2
          }
        });

        // Add base name labels
        mapInstance.addLayer({
          id: `${layerId}-name-labels`,
          type: 'symbol',
          source: srcId,
          layout: {
            'text-field': ['get', 'name'],
            'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
            'text-offset': [0, 2],
            'text-anchor': 'top',
            'text-size': 11
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': '#000000',
            'text-halo-width': 1
          }
        });

        // Add click handlers for info popups
        mapInstance.on('click', layerId, (e) => {
          const features = e.features;
          if (features && features.length > 0) {
            const feature = features[0];
            const properties = feature.properties;
            
            // Create popup content
            const popupContent = `
              <div style="padding: 10px; max-width: 300px;">
                <h3 style="margin: 0 0 8px 0; color: #1f2937; font-size: 16px; font-weight: bold;">
                  ${properties.name}
                </h3>
                <div style="margin: 4px 0; color: #374151;">
                  <strong>Country:</strong> ${properties.country}
                </div>
                <div style="margin: 4px 0; color: #374151;">
                  <strong>Type:</strong> ${properties.type}
                </div>
                <div style="margin: 8px 0 0 0; color: #4b5563; font-size: 14px; line-height: 1.4;">
                  ${properties.info}
                </div>
              </div>
            `;
            
            // Create popup
            new maplibregl.Popup()
              .setLngLat(e.lngLat)
              .setHTML(popupContent)
              .addTo(mapInstance);
          }
        });

        // Change cursor on hover
        mapInstance.on('mouseenter', layerId, () => {
          mapInstance.getCanvas().style.cursor = 'pointer';
        });

        mapInstance.on('mouseleave', layerId, () => {
          mapInstance.getCanvas().style.cursor = '';
        });
      }
    };

    // ── Phase 2: SAR mode — dim optical layer when SAR is active ──
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const layerId = 'wayback-release-lyr';
      if (map.current.getLayer(layerId)) {
        map.current.setPaintProperty(layerId, 'raster-opacity', showSAROnly ? 0.3 : 1);
      }
    }, [showSAROnly, mapLoaded]);

    // ── Phase 2: Spectral Index Overlay ──────────────────────────
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const m = map.current;
      const srcId = 'spectral-overlay-src';
      const layerId = 'spectral-overlay-lyr';

      // Remove existing spectral layer
      if (m.getLayer(layerId)) m.removeLayer(layerId);
      if (m.getSource(srcId)) m.removeSource(srcId);

      if (!spectralIndex || !activeReleaseId) return;

      // Determine the view_id from the active release
      const release = releases.find(r => r.id === activeReleaseId);
      let viewId = '';
      if (release) {
        viewId = (release as any)?.view_id || '';
        // Try to extract from tileTemplate if view_id not directly available
        if (!viewId && release.tileTemplate) {
          const match = release.tileTemplate.match(/\/eosda\/([^/]+)/);
          if (match) viewId = match[1];
        }
      }
      if (!viewId && typeof activeReleaseId === 'string') {
        // Use the release ID itself for EOSDA/Copernicus scenes
        viewId = activeReleaseId.replace('eosda_sentinel2_', '').replace('copernicus_', '');
      }

      if (!viewId) {
        console.log('No view_id available for spectral overlay — requires Sentinel-2 scene');
        return;
      }

      try {
        const tileUrl = `/api/geoint/spectral/tiles/${viewId}/${spectralIndex}/{z}/{x}/{y}`;
        m.addSource(srcId, { type: 'raster', tiles: [tileUrl], tileSize: 256 });
        m.addLayer({
          id: layerId,
          type: 'raster',
          source: srcId,
          paint: { 'raster-opacity': 0.75 },
          minzoom: 0,
          maxzoom: 18,
        });
        console.log(`Spectral ${spectralIndex.toUpperCase()} overlay added for ${viewId}`);
      } catch (e) {
        console.error('Failed to add spectral overlay:', e);
      }
    }, [spectralIndex, activeReleaseId, mapLoaded, releases]);

    // ── Phase 2: FIRMS Thermal Anomaly Overlay ────────────────────
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const m = map.current;
      const srcId = 'firms-thermal-src';
      const layerId = 'firms-thermal-lyr';
      const heatLayerId = 'firms-thermal-heat';

      // Remove existing FIRMS layers
      if (m.getLayer(heatLayerId)) m.removeLayer(heatLayerId);
      if (m.getLayer(layerId)) m.removeLayer(layerId);
      if (m.getSource(srcId)) m.removeSource(srcId);

      if (!showFIRMS) return;

      // Fetch thermal anomalies for the current map viewport
      const bounds = m.getBounds();
      const west = bounds.getWest();
      const south = bounds.getSouth();
      const east = bounds.getEast();
      const north = bounds.getNorth();

      const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || '';
      fetch(
        `/api/geoint/firms/anomalies/geojson?west=${west}&south=${south}&east=${east}&north=${north}&days=2`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
        .then(r => r.json())
        .then(geojson => {
          if (!map.current || !geojson?.features) return;
          try {
            map.current.addSource(srcId, { type: 'geojson', data: geojson });
            // Heat-like circle layer
            map.current.addLayer({
              id: layerId,
              type: 'circle',
              source: srcId,
              paint: {
                'circle-radius': ['interpolate', ['linear'], ['get', 'frp'], 0, 4, 100, 12, 500, 20],
                'circle-color': ['interpolate', ['linear'], ['get', 'frp'],
                  0, '#ffeb3b',
                  50, '#ff9800',
                  200, '#f44336',
                  500, '#b71c1c'
                ],
                'circle-opacity': 0.8,
                'circle-blur': 0.3,
              },
            });
            console.log(`FIRMS overlay: ${geojson.features.length} thermal anomalies`);
          } catch (e) {
            console.error('Failed to add FIRMS overlay:', e);
          }
        })
        .catch(err => console.error('FIRMS fetch error:', err));

      // Refresh when map moves
      const onMoveEnd = () => {
        if (!showFIRMS || !map.current) return;
        const b = map.current.getBounds();
        fetch(
          `/api/geoint/firms/anomalies/geojson?west=${b.getWest()}&south=${b.getSouth()}&east=${b.getEast()}&north=${b.getNorth()}&days=2`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
          .then(r => r.json())
          .then(geojson => {
            if (!map.current) return;
            const src = map.current.getSource(srcId) as any;
            if (src && geojson?.features) {
              src.setData(geojson);
            }
          })
          .catch(() => {});
      };
      m.on('moveend', onMoveEnd);
      return () => { m.off('moveend', onMoveEnd); };
    }, [showFIRMS, mapLoaded]);

    // ── Phase 2: Object Detection Markers ─────────────────────────
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const m = map.current;
      const srcId = 'object-detect-src';
      const layerId = 'object-detect-lyr';
      const outlineLayerId = 'object-detect-outline-lyr';
      const labelLayerId = 'object-detect-label-lyr';

      if (m.getLayer(labelLayerId)) m.removeLayer(labelLayerId);
      if (m.getLayer(outlineLayerId)) m.removeLayer(outlineLayerId);
      if (m.getLayer(layerId)) m.removeLayer(layerId);
      if (m.getSource(srcId)) m.removeSource(srcId);

      if (!objectDetectionResults?.tile_results) return;

      // Convert pixel bbox → lat/lon corner using Web Mercator tile math
      const pixelToGeo = (z: number, tx: number, ty: number, px: number, py: number): [number, number] => {
        const n = Math.pow(2, z);
        const lon = (tx + px / 256) / n * 360 - 180;
        const latRad = Math.atan(Math.sinh(Math.PI * (1 - 2 * (ty + py / 256) / n)));
        return [lon, latRad * 180 / Math.PI];
      };

      // Class → color mapping
      const classColor = (cls: string): string => {
        if (cls === 'vessel') return '#00e5ff';
        if (cls === 'aircraft') return '#ff9500';
        if (cls === 'vehicle') return '#00ff88';
        if (cls === 'infrastructure') return '#ff6b6b';
        return '#a78bfa';
      };

      const features: any[] = [];
      for (const tileResult of objectDetectionResults.tile_results) {
        const { tile_z, tile_x, tile_y } = tileResult;
        for (const det of tileResult.detections || []) {
          if (!det.lat || !det.lon) continue;

          // Compute bounding box corners in lat/lon using pixel coords
          const x1 = det.x - det.width / 2;
          const y1 = det.y - det.height / 2;
          const x2 = det.x + det.width / 2;
          const y2 = det.y + det.height / 2;
          const [lon1, lat1] = pixelToGeo(tile_z, tile_x, tile_y, x1, y1);
          const [lon2, lat2] = pixelToGeo(tile_z, tile_x, tile_y, x2, y2);

          features.push({
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [[[lon1, lat1], [lon2, lat1], [lon2, lat2], [lon1, lat2], [lon1, lat1]]],
            },
            properties: {
              class: det.class_name,
              confidence: Math.round(det.confidence * 100),
              color: classColor(det.class_name),
              lat: det.lat ?? null,
              lon: det.lon ?? null,
              adsb_callsign: det.adsb_callsign ?? null,
              adsb_icao24: det.adsb_icao24 ?? null,
              adsb_altitude_ft: det.adsb_altitude_ft ?? null,
              adsb_on_ground: det.adsb_on_ground ?? null,
              transponder_off: det.transponder_off ?? false,
              crop_b64: det.crop_b64 ?? null,
            },
          });
        }
      }

      if (features.length === 0) return;

      const geojson = { type: 'FeatureCollection' as const, features };

      try {
        m.addSource(srcId, { type: 'geojson', data: geojson });

        // Semitransparent fill
        m.addLayer({
          id: layerId,
          type: 'fill',
          source: srcId,
          paint: {
            'fill-color': ['get', 'color'],
            'fill-opacity': 0.18,
          },
        });

        // Bounding box outline
        m.addLayer({
          id: outlineLayerId,
          type: 'line',
          source: srcId,
          paint: {
            'line-color': ['get', 'color'],
            'line-width': 2,
          },
        });

        // Labels at box centroid
        m.addLayer({
          id: labelLayerId,
          type: 'symbol',
          source: srcId,
          layout: {
            'text-field': ['concat', ['get', 'class'], ' ', ['to-string', ['get', 'confidence']], '%'],
            'text-size': 13,
            'text-anchor': 'center',
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': '#000000',
            'text-halo-width': 2,
          },
        });

        console.log(`Object detection overlay: ${features.length} bbox(es)`);

        // Analyst assist: click on a bbox → dark popup with detection details
        const onDetClick = (e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
          const feat = e.features?.[0];
          if (!feat) return;
          const p = feat.properties as Record<string, any>;

          const cropHtml = p.crop_b64
            ? `<img src="data:image/jpeg;base64,${p.crop_b64}" style="width:100%;max-width:200px;border-radius:4px;margin-top:8px;display:block;">`
            : '';
          const adsbHtml = p.adsb_callsign
            ? `<div style="margin-top:7px;padding-top:7px;border-top:1px solid #1e293b;font-size:11px;">
                 <span style="color:#94a3b8;">ADS-B</span>&nbsp;
                 <span style="color:#fb923c;font-weight:700;">${p.adsb_callsign}</span>
                 ${p.adsb_icao24 ? `<span style="color:#475569;"> · ${p.adsb_icao24}</span>` : ''}
                 ${p.adsb_altitude_ft ? `<div style="color:#64748b;font-size:10px;margin-top:2px;">${p.adsb_on_ground ? 'On ground' : `${p.adsb_altitude_ft.toLocaleString()} ft`}</div>` : ''}
               </div>`
            : p.transponder_off
              ? `<div style="margin-top:6px;padding:3px 7px;background:rgba(124,45,18,0.3);border:1px solid #7c2d12;border-radius:3px;font-size:10px;color:#fca5a5;">No ADS-B transponder signal</div>`
              : '';
          const coordHtml = (p.lat && p.lon)
            ? `<div style="font-size:10px;color:#475569;margin-top:3px;">${Number(p.lat).toFixed(5)}&deg;N &nbsp;${Number(p.lon).toFixed(5)}&deg;E</div>`
            : '';

          detectionPopupRef.current?.remove();
          detectionPopupRef.current = new maplibregl.Popup({
            closeOnClick: true, maxWidth: '240px', offset: [0, -4],
          })
            .setLngLat(e.lngLat)
            .setHTML(
              `<div style="margin:-10px -10px -15px;padding:10px 12px 12px;background:#0f172a;color:#e2e8f0;font-family:ui-monospace,monospace;font-size:12px;border-radius:3px;min-width:160px;">
                <div style="display:flex;align-items:center;gap:6px;margin-bottom:2px;">
                  <span style="width:10px;height:10px;border-radius:2px;background:${p.color};display:inline-block;flex-shrink:0;"></span>
                  <span style="font-weight:700;text-transform:uppercase;letter-spacing:.5px;">${p.class}</span>
                  <span style="margin-left:auto;color:#4ade80;font-weight:600;">${p.confidence}%</span>
                </div>
                ${coordHtml}${adsbHtml}${cropHtml}
              </div>`
            )
            .addTo(m);
        };
        const onDetEnter = () => { m.getCanvas().style.cursor = 'crosshair'; };
        const onDetLeave = () => { m.getCanvas().style.cursor = ''; };
        m.on('click', layerId, onDetClick);
        m.on('mouseenter', layerId, onDetEnter);
        m.on('mouseleave', layerId, onDetLeave);

      } catch (e) {
        console.error('Failed to add detection overlay:', e);
      }
    }, [objectDetectionResults, mapLoaded]);

    // ── Change Detection Overlay ─────────────────────────────────
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const m = map.current;
      const srcId = 'change-detect-src';
      const fillLayerId = 'change-detect-fill';
      const outlineLayerId = 'change-detect-outline';
      const labelLayerId = 'change-detect-label';

      // Remove existing layers
      if (m.getLayer(labelLayerId)) m.removeLayer(labelLayerId);
      if (m.getLayer(outlineLayerId)) m.removeLayer(outlineLayerId);
      if (m.getLayer(fillLayerId)) m.removeLayer(fillLayerId);
      if (m.getSource(srcId)) m.removeSource(srcId);

      if (!changeDetectionJob?.metrics || !changeDetectionJob?.request?.aoi) return;

      const changePct = changeDetectionJob.metrics.change_percentage || 0;
      const changedArea = changeDetectionJob.metrics.changed_area || 0;
      const tilesAnalyzed = changeDetectionJob.metrics.tiles_analyzed || 0;

      // Color by severity: green (<10%), yellow (10-40%), orange (40-70%), red (>70%)
      const fillColor = changePct > 70 ? '#ef4444' : changePct > 40 ? '#f97316' : changePct > 10 ? '#eab308' : '#22c55e';
      const fillOpacity = Math.max(0.15, Math.min(0.45, changePct / 200));

      // Strip non-GeoJSON fields (id, name, aoi_id) that MapLibre rejects in geometry
      const rawAoi = changeDetectionJob.request.aoi;
      const aoi = rawAoi?.type && rawAoi?.coordinates
        ? { type: rawAoi.type, coordinates: rawAoi.coordinates }
        : rawAoi;
      const geojson = {
        type: 'FeatureCollection' as const,
        features: [{
          type: 'Feature' as const,
          geometry: aoi,
          properties: {
            change_pct: changePct.toFixed(1),
            changed_area: changedArea.toFixed(2),
            tiles: tilesAnalyzed,
            label: `${changePct.toFixed(1)}% change (${changedArea.toFixed(2)} km²)`,
          },
        }],
      };

      try {
        m.addSource(srcId, { type: 'geojson', data: geojson });

        // Translucent fill
        m.addLayer({
          id: fillLayerId,
          type: 'fill',
          source: srcId,
          paint: {
            'fill-color': fillColor,
            'fill-opacity': fillOpacity,
          },
        });

        // Dashed outline
        m.addLayer({
          id: outlineLayerId,
          type: 'line',
          source: srcId,
          paint: {
            'line-color': fillColor,
            'line-width': 2.5,
            'line-dasharray': [4, 3],
          },
        });

        // Label at centroid
        m.addLayer({
          id: labelLayerId,
          type: 'symbol',
          source: srcId,
          layout: {
            'text-field': ['get', 'label'],
            'text-size': 13,
            'text-font': ['Open Sans Bold'],
            'text-anchor': 'center',
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': fillColor,
            'text-halo-width': 2,
          },
        });

        console.log(`Change detection overlay: ${changePct}% change over ${changedArea} km²`);
      } catch (e) {
        console.error('Failed to add change detection overlay:', e);
      }
    }, [changeDetectionJob, mapLoaded]);

    // ── Entity Pins Overlay (2D + 3D) ─────────────────────────
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const m = map.current;
      const srcId = 'entity-pins-src';
      const circleLayerId = 'entity-pins-circle';
      const labelLayerId = 'entity-pins-label';
      const pulseLayerId = 'entity-pins-pulse';
      const extrusionLayerId = 'entity-pins-3d';

      // Remove existing layers
      [labelLayerId, pulseLayerId, circleLayerId, extrusionLayerId].forEach(id => {
        if (m.getLayer(id)) m.removeLayer(id);
      });
      if (m.getSource(srcId)) m.removeSource(srcId);

      if (!entityPins || entityPins.length === 0) return;

      const TYPE_COLORS: Record<string, string> = {
        VESSEL: '#3b82f6', AIRCRAFT: '#06b6d4', PERSON: '#f97316',
        ORGANIZATION: '#a855f7', LOCATION: '#22c55e', EVENT: '#ef4444', ASSET: '#eab308',
      };
      // Heights for 3D extrusion per entity type (meters)
      const TYPE_HEIGHTS: Record<string, number> = {
        VESSEL: 30, AIRCRAFT: 120, PERSON: 15,
        ORGANIZATION: 60, LOCATION: 25, EVENT: 50, ASSET: 40,
      };

      const features = entityPins
        .filter(p => p.lat && p.lon)
        .map(pin => {
          // Create a small polygon (hexagon) around the point for 3D extrusion
          const r = 0.00015; // ~15m radius
          const cx = pin.lon, cy = pin.lat;
          const hexCoords = [];
          for (let i = 0; i < 6; i++) {
            const angle = (Math.PI / 3) * i - Math.PI / 6;
            hexCoords.push([cx + r * Math.cos(angle), cy + r * Math.sin(angle)]);
          }
          hexCoords.push(hexCoords[0]); // close ring

          return {
            type: 'Feature' as const,
            geometry: { type: 'Polygon' as const, coordinates: [hexCoords] },
            properties: {
              entity_id: pin.entity_id,
              name: pin.name,
              entity_type: pin.entity_type,
              color: TYPE_COLORS[pin.entity_type] || '#6b7280',
              height: TYPE_HEIGHTS[pin.entity_type] || 25,
              priority: pin.priority || 3,
              is_watchlisted: pin.is_watchlisted ? 1 : 0,
              obs_count: pin.observation_count || 0,
              lon: pin.lon,
              lat: pin.lat,
            },
          };
        });

      // Also create point features for labels/circles
      const pointFeatures = entityPins
        .filter(p => p.lat && p.lon)
        .map(pin => ({
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [pin.lon, pin.lat] },
          properties: {
            entity_id: pin.entity_id,
            name: pin.name,
            entity_type: pin.entity_type,
            color: TYPE_COLORS[pin.entity_type] || '#6b7280',
            priority: pin.priority || 3,
            is_watchlisted: pin.is_watchlisted ? 1 : 0,
            obs_count: pin.observation_count || 0,
          },
        }));

      try {
        m.addSource(srcId, {
          type: 'geojson',
          data: { type: 'FeatureCollection' as const, features: [...features, ...pointFeatures] },
        });

        // 3D extruded hexagonal pillars (visible at all times, dramatic in 3D)
        m.addLayer({
          id: extrusionLayerId,
          type: 'fill-extrusion',
          source: srcId,
          filter: ['==', ['geometry-type'], 'Polygon'],
          paint: {
            'fill-extrusion-color': ['get', 'color'],
            'fill-extrusion-height': ['get', 'height'],
            'fill-extrusion-base': 0,
            'fill-extrusion-opacity': 0.85,
          },
        } as any);

        // Outer pulse ring for watchlisted entities
        m.addLayer({
          id: pulseLayerId,
          type: 'circle',
          source: srcId,
          filter: ['all', ['==', ['geometry-type'], 'Point'], ['==', ['get', 'is_watchlisted'], 1]],
          paint: {
            'circle-radius': 18,
            'circle-color': 'transparent',
            'circle-stroke-width': 2.5,
            'circle-stroke-color': '#f59e0b',
            'circle-stroke-opacity': 0.7,
          },
        });

        // Main circle (2D fallback + center dot)
        m.addLayer({
          id: circleLayerId,
          type: 'circle',
          source: srcId,
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['get', 'obs_count'], 0, 5, 5, 7, 20, 10],
            'circle-color': ['get', 'color'],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.9,
          },
        });

        // Entity type icon + name label
        m.addLayer({
          id: labelLayerId,
          type: 'symbol',
          source: srcId,
          filter: ['==', ['geometry-type'], 'Point'],
          layout: {
            'text-field': ['concat',
              ['match', ['get', 'entity_type'],
                'VESSEL', '[V] ',
                'AIRCRAFT', '[A] ',
                'PERSON', '[P] ',
                'ORGANIZATION', '[O] ',
                'LOCATION', '[L] ',
                'EVENT', '[!] ',
                'ASSET', '[X] ',
                ''],
              ['get', 'name']
            ],
            'text-size': 11,
            'text-offset': [0, 2.0],
            'text-anchor': 'top',
            'text-max-width': 14,
            'text-font': ['Open Sans Bold'],
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': '#000000',
            'text-halo-width': 1.5,
          },
        });

        // Click handler on both extrusion and circle (uses ref to avoid re-creating overlay on every render)
        const clickHandler = (e: any) => {
          const entityId = e.features?.[0]?.properties?.entity_id;
          if (entityId && onEntityPinClickRef.current) onEntityPinClickRef.current(entityId);
        };
        m.on('click', extrusionLayerId, clickHandler);
        m.on('click', circleLayerId, clickHandler);
        m.on('mouseenter', extrusionLayerId, () => { m.getCanvas().style.cursor = 'pointer'; });
        m.on('mouseleave', extrusionLayerId, () => { m.getCanvas().style.cursor = ''; });
        m.on('mouseenter', circleLayerId, () => { m.getCanvas().style.cursor = 'pointer'; });
        m.on('mouseleave', circleLayerId, () => { m.getCanvas().style.cursor = ''; });

        console.log(`Entity pins overlay: ${entityPins.length} entities (3D hexagons + labels)`);
      } catch (e) {
        console.error('Failed to add entity pins overlay:', e);
      }
    }, [entityPins, mapLoaded]);

    // ── Live Aircraft Showcase Overlay ─────────────────────────
    // Shown around the current view on map load (no AOI required) to demonstrate
    // live tracking capability. Populated by GEOINT_Wayback.tsx's onMapReady fetch
    // against /geoint/aircraft (FR24, budget-guarded, OpenSky fallback).
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const m = map.current;
      const srcId = 'live-aircraft-src';
      const circleLayerId = 'live-aircraft-circle';
      const labelLayerId = 'live-aircraft-label';
      const headingLayerId = 'live-aircraft-heading';

      [labelLayerId, headingLayerId, circleLayerId].forEach(id => { if (m.getLayer(id)) m.removeLayer(id); });
      if (m.getSource(srcId)) m.removeSource(srcId);

      if (!liveAircraft || liveAircraft.length === 0) return;

      const features = liveAircraft
        .filter(a => a.lat != null && a.lon != null)
        .map(a => ({
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [a.lon, a.lat] },
          properties: { id: a.id, label: a.label || a.id, type: a.type || '', track: a.track ?? null },
        }));

      try {
        m.addSource(srcId, { type: 'geojson', data: { type: 'FeatureCollection' as const, features } });
        m.addLayer({
          id: circleLayerId,
          type: 'circle',
          source: srcId,
          paint: {
            'circle-radius': 5,
            'circle-color': '#f97316',
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.9,
          },
        });
        // Heading arrow — direction of travel (FR24 `track` / OpenSky `true_track`).
        m.addLayer({
          id: headingLayerId,
          type: 'symbol',
          source: srcId,
          filter: ['has', 'track'],
          layout: {
            'text-field': '▲',
            'text-size': 14,
            'text-rotate': ['coalesce', ['get', 'track'], 0],
            'text-rotation-alignment': 'map',
            'text-allow-overlap': true,
            'text-ignore-placement': true,
          },
          paint: { 'text-color': '#f97316', 'text-halo-color': '#000000', 'text-halo-width': 0.8 },
        });
        m.addLayer({
          id: labelLayerId,
          type: 'symbol',
          source: srcId,
          layout: {
            'text-field': ['get', 'label'],
            'text-size': 10,
            'text-offset': [0, 1.2],
            'text-anchor': 'top',
            'text-font': ['Open Sans Bold'],
          },
          paint: { 'text-color': '#fdba74', 'text-halo-color': '#000000', 'text-halo-width': 1.2 },
        });
      } catch (e) {
        console.error('Failed to add live aircraft overlay:', e);
      }
    }, [liveAircraft, mapLoaded]);

    // ── Live Vessels Showcase Overlay ──────────────────────────
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const m = map.current;
      const srcId = 'live-vessels-src';
      const circleLayerId = 'live-vessels-circle';
      const labelLayerId = 'live-vessels-label';
      const headingLayerId = 'live-vessels-heading';

      [labelLayerId, headingLayerId, circleLayerId].forEach(id => { if (m.getLayer(id)) m.removeLayer(id); });
      if (m.getSource(srcId)) m.removeSource(srcId);

      if (!liveVessels || liveVessels.length === 0) return;

      const features = liveVessels
        .filter(v => v.lat != null && v.lon != null)
        .map(v => ({
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [v.lon, v.lat] },
          properties: { id: v.id, label: v.label || v.id, type: v.type || '', course: v.course ?? null },
        }));

      try {
        m.addSource(srcId, { type: 'geojson', data: { type: 'FeatureCollection' as const, features } });
        m.addLayer({
          id: circleLayerId,
          type: 'circle',
          source: srcId,
          paint: {
            'circle-radius': 5,
            'circle-color': '#3b82f6',
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.9,
          },
        });
        // Heading arrow — direction of travel (course over ground).
        m.addLayer({
          id: headingLayerId,
          type: 'symbol',
          source: srcId,
          filter: ['has', 'course'],
          layout: {
            'text-field': '▲',
            'text-size': 14,
            'text-rotate': ['coalesce', ['get', 'course'], 0],
            'text-rotation-alignment': 'map',
            'text-allow-overlap': true,
            'text-ignore-placement': true,
          },
          paint: { 'text-color': '#3b82f6', 'text-halo-color': '#000000', 'text-halo-width': 0.8 },
        });
        m.addLayer({
          id: labelLayerId,
          type: 'symbol',
          source: srcId,
          layout: {
            'text-field': ['get', 'label'],
            'text-size': 10,
            'text-offset': [0, 1.2],
            'text-anchor': 'top',
            'text-font': ['Open Sans Bold'],
          },
          paint: { 'text-color': '#93c5fd', 'text-halo-color': '#000000', 'text-halo-width': 1.2 },
        });
      } catch (e) {
        console.error('Failed to add live vessels overlay:', e);
      }
    }, [liveVessels, mapLoaded]);

    // ── Live aircraft/vessel click handlers ────────────────────
    // Registered once (not re-bound every time the data refreshes) — reads the
    // latest callback via a ref so the layers above can be freely removed/re-added
    // without ever double-registering a listener.
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const m = map.current;

      const aircraftClick = (e: any) => {
        const id = e.features?.[0]?.properties?.id;
        if (id && onLiveAircraftClickRef.current) onLiveAircraftClickRef.current(id);
      };
      const vesselClick = (e: any) => {
        const id = e.features?.[0]?.properties?.id;
        if (id && onLiveVesselClickRef.current) onLiveVesselClickRef.current(id);
      };
      const pointerEnter = () => { m.getCanvas().style.cursor = 'pointer'; };
      const pointerLeave = () => { m.getCanvas().style.cursor = ''; };

      m.on('click', 'live-aircraft-circle', aircraftClick);
      m.on('mouseenter', 'live-aircraft-circle', pointerEnter);
      m.on('mouseleave', 'live-aircraft-circle', pointerLeave);
      m.on('click', 'live-vessels-circle', vesselClick);
      m.on('mouseenter', 'live-vessels-circle', pointerEnter);
      m.on('mouseleave', 'live-vessels-circle', pointerLeave);

      return () => {
        m.off('click', 'live-aircraft-circle', aircraftClick);
        m.off('mouseenter', 'live-aircraft-circle', pointerEnter);
        m.off('mouseleave', 'live-aircraft-circle', pointerLeave);
        m.off('click', 'live-vessels-circle', vesselClick);
        m.off('mouseenter', 'live-vessels-circle', pointerEnter);
        m.off('mouseleave', 'live-vessels-circle', pointerLeave);
      };
    }, [mapLoaded]);

    // ── Selected flight path (origin → current position → destination) ────
    // Origin/destination resolved offline server-side (services/airport_lookup.py,
    // no API cost) and passed in via selectedFlightPath. The flown leg (origin →
    // current) renders solid; the remaining leg (current → destination) dashed,
    // since we don't know the actual future route, just where it's headed.
    useEffect(() => {
      if (!map.current || !mapLoaded) return;
      const m = map.current;
      const srcFlown = 'flight-path-flown-src';
      const srcRemaining = 'flight-path-remaining-src';
      const srcAirports = 'flight-path-airports-src';
      const lineFlownId = 'flight-path-flown-line';
      const lineRemainingId = 'flight-path-remaining-line';
      const airportCircleId = 'flight-path-airports-circle';
      const airportLabelId = 'flight-path-airports-label';

      [lineFlownId, lineRemainingId, airportLabelId, airportCircleId].forEach(id => {
        if (m.getLayer(id)) m.removeLayer(id);
      });
      [srcFlown, srcRemaining, srcAirports].forEach(id => { if (m.getSource(id)) m.removeSource(id); });

      if (!selectedFlightPath) return;
      const { origin, current, destination } = selectedFlightPath;
      if (!current?.lat || !current?.lon) return;

      try {
        const flownCoords = origin ? [[origin.lon, origin.lat], [current.lon, current.lat]] : [];
        const remainingCoords = destination ? [[current.lon, current.lat], [destination.lon, destination.lat]] : [];

        m.addSource(srcFlown, {
          type: 'geojson',
          data: { type: 'Feature', geometry: { type: 'LineString', coordinates: flownCoords }, properties: {} },
        });
        m.addSource(srcRemaining, {
          type: 'geojson',
          data: { type: 'Feature', geometry: { type: 'LineString', coordinates: remainingCoords }, properties: {} },
        });
        if (flownCoords.length) {
          m.addLayer({
            id: lineFlownId, type: 'line', source: srcFlown,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': '#f97316', 'line-width': 2.5, 'line-opacity': 0.85 },
          });
        }
        if (remainingCoords.length) {
          m.addLayer({
            id: lineRemainingId, type: 'line', source: srcRemaining,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': '#f97316', 'line-width': 2, 'line-opacity': 0.5, 'line-dasharray': [2, 2] },
          });
        }

        const airportFeatures = [
          origin && { type: 'Feature' as const, geometry: { type: 'Point' as const, coordinates: [origin.lon, origin.lat] }, properties: { label: origin.name || 'Origin' } },
          destination && { type: 'Feature' as const, geometry: { type: 'Point' as const, coordinates: [destination.lon, destination.lat] }, properties: { label: destination.name || 'Destination' } },
        ].filter(Boolean) as any[];

        if (airportFeatures.length) {
          m.addSource(srcAirports, { type: 'geojson', data: { type: 'FeatureCollection', features: airportFeatures } });
          m.addLayer({
            id: airportCircleId, type: 'circle', source: srcAirports,
            paint: { 'circle-radius': 4, 'circle-color': '#ffffff', 'circle-stroke-width': 2, 'circle-stroke-color': '#f97316' },
          });
          m.addLayer({
            id: airportLabelId, type: 'symbol', source: srcAirports,
            layout: { 'text-field': ['get', 'label'], 'text-size': 10, 'text-offset': [0, -1.2], 'text-anchor': 'bottom', 'text-font': ['Open Sans Bold'] },
            paint: { 'text-color': '#ffffff', 'text-halo-color': '#000000', 'text-halo-width': 1.2 },
          });
        }
      } catch (e) {
        console.error('Failed to add flight path overlay:', e);
      }
    }, [selectedFlightPath, mapLoaded]);

    // ── Selected entity highlight (bbox ring + label on map) ──────
    useEffect(() => {
      entityHighlightMarkerRef.current?.remove();
      entityHighlightMarkerRef.current = null;
      if (!map.current || !mapLoaded || !selectedEntityHighlight?.lat || !selectedEntityHighlight?.lon) return;
      const m = map.current;

      const typeColors: Record<string, string> = {
        AIRCRAFT: '#f97316', VESSEL: '#00e5ff', VEHICLE: '#00ff88',
        ASSET: '#a78bfa', EVENT: '#ef4444',
      };
      const col = typeColors[(selectedEntityHighlight.entity_type || '').toUpperCase()] || '#facc15';

      // Pulsing DOM marker element
      const el = document.createElement('div');
      el.style.cssText = [
        'pointer-events:none',
        `width:64px`,
        `height:64px`,
        `border:2px solid ${col}`,
        `border-radius:50%`,
        `box-shadow:0 0 0 4px ${col}30, 0 0 12px ${col}60`,
        `animation:entity-ring-pulse 1.6s ease-out infinite`,
      ].join(';');

      // Inject animation once
      if (!document.getElementById('entity-ring-style')) {
        const style = document.createElement('style');
        style.id = 'entity-ring-style';
        style.textContent = `@keyframes entity-ring-pulse{0%{box-shadow:0 0 0 0 ${col}80}70%{box-shadow:0 0 0 12px transparent}100%{box-shadow:0 0 0 0 transparent}}`;
        document.head.appendChild(style);
      }

      // Label above
      const label = document.createElement('div');
      label.style.cssText = [
        'position:absolute',
        'top:-22px',
        'left:50%',
        'transform:translateX(-50%)',
        'white-space:nowrap',
        `color:${col}`,
        'font-family:ui-monospace,monospace',
        'font-size:10px',
        'font-weight:700',
        'letter-spacing:.5px',
        `text-shadow:0 1px 3px #000,0 0 6px #000`,
        'pointer-events:none',
      ].join(';');
      label.textContent = selectedEntityHighlight.name?.slice(0, 32) || selectedEntityHighlight.entity_type;
      el.appendChild(label);

      entityHighlightMarkerRef.current = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([selectedEntityHighlight.lon, selectedEntityHighlight.lat])
        .addTo(m);

      m.flyTo({ center: [selectedEntityHighlight.lon, selectedEntityHighlight.lat], zoom: 15, duration: 900 });

      return () => { entityHighlightMarkerRef.current?.remove(); };
    }, [selectedEntityHighlight, mapLoaded]);

    const classColor = (cls: string): string => {
      if (cls === 'vessel') return '#00e5ff';
      if (cls === 'aircraft') return '#f97316';
      if (cls === 'vehicle') return '#00ff88';
      if (cls === 'infrastructure') return '#ff6b6b';
      return '#a78bfa';
    };

    return (
      <div className="w-full h-full relative" style={{ minHeight: '400px' }}>
        <div
          ref={mapContainer}
          className="absolute inset-0"
          style={{
            pointerEvents: 'auto',
            zIndex: drawingMode ? 100 : 1,
          }}
        />

        {/* ── Activity overlay: detection type counts ────────────── */}
        {activityCounts && (
          <div className="absolute bottom-8 left-3 z-10 bg-slate-900/90 border border-slate-700/80 rounded-lg py-2 px-3 min-w-[148px] backdrop-blur-sm pointer-events-none">
            <div className="text-[9px] text-slate-400 uppercase tracking-widest mb-2 font-semibold">Activity</div>
            {Object.entries(activityCounts)
              .sort(([, a], [, b]) => (b as number) - (a as number))
              .map(([cls, count]) => {
                const total = Object.values(activityCounts).reduce((s, v) => s + (v as number), 0);
                const pct = Math.round(((count as number) / total) * 100);
                return (
                  <div key={cls} className="flex items-center gap-1.5 mb-1">
                    <span style={{ background: classColor(cls), width: 7, height: 7, borderRadius: 1, display: 'inline-block', flexShrink: 0 }} />
                    <span className="text-slate-300 text-[10px] capitalize flex-1 truncate" style={{ maxWidth: 72 }}>{cls}</span>
                    <div className="flex items-center gap-1">
                      <div className="w-16 h-1 bg-slate-700 rounded-full overflow-hidden">
                        <div style={{ width: `${pct}%`, background: classColor(cls) }} className="h-full rounded-full" />
                      </div>
                      <span className="text-white text-[10px] font-bold w-4 text-right">{count as number}</span>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  }
);

WaybackMap.displayName = 'WaybackMap';

export default WaybackMap;
